import { useCallback, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import useApi from '../../hooks/useApi'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import Badge from '../badge'
import Icon from '../icon'
import CharityPickerModal from '../Charity/CharityPickerModal'

export default function VPFVolunteerAcknowledgementModal({ show, onHide }) {
  const { t } = useTranslation()
  const { currentUser, setCurrentUser } = useCurrentUser()
  const { updateUser } = useApi()

  const [phase, setPhase] = useState('acknowledgement')
  const [savingCharity, setSavingCharity] = useState(false)

  useEffect(() => {
    if (show) {
      setPhase('acknowledgement')
    }
  }, [show])

  const handleCloseAll = useCallback(() => {
    setPhase('acknowledgement')
    onHide()
  }, [onHide])

  const handleSelectCharity = useCallback(
    async charity => {
      if (savingCharity) return

      const charityId = charity?.id || charity?.organization_id
      const charityName = charity?.organization_name

      if (currentUser?.favorite_charity_id === charityId) {
        return
      }

      setSavingCharity(true)
      try {
        const response = await updateUser(currentUser.id, {
          favorite_charity_id: charityId,
        })

        if (response.status === 200) {
          const updatedUser = response.data || {
            ...currentUser,
            favorite_charity_id: charityId,
            favorite_charity: {
              organization_name: charityName,
            },
          }
          setCurrentUser(updatedUser)
        }
      } catch (error) {
        console.error('[VPFVolunteerAcknowledgementModal] updateUser:', error)
      } finally {
        setSavingCharity(false)
      }
    },
    [currentUser, savingCharity, setCurrentUser, updateUser],
  )

  return (
    <>
      <Modal
        show={show && phase === 'acknowledgement'}
        onHide={handleCloseAll}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex flex-column w-100 gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Badge className="badge-beta" />
              <span className="d-flex align-items-center gap-2">
                <Icon name="hand-heart" className="text-danger" />
                {t('event.vpf.volunteer_signup_acknowledgement.title')}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p className="mb-0">
            {t('event.vpf.volunteer_signup_acknowledgement.message')}
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 flex-nowrap flex-wrap gap-2 justify-content-between">
          <Button variant="outline-secondary" onClick={handleCloseAll}>
            {t('event.vpf.volunteer_signup_acknowledgement.close')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setPhase('charity')}
            disabled={savingCharity}
          >
            {t(
              'event.vpf.volunteer_signup_acknowledgement.select_favorite_charity',
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <CharityPickerModal
        show={show && phase === 'charity'}
        onHide={handleCloseAll}
        onSelectCharity={handleSelectCharity}
        currentDefaultCharityId={currentUser?.favorite_charity_id}
        disabled={savingCharity}
      />
    </>
  )
}
